import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Loader2, User, MapPin, Briefcase, Star, ChevronLeft, Plus, X,
  CheckCircle, Clock, AlertTriangle, Shield, Eye, Search, Zap,
  BadgeCheck, Award, Crown
} from 'lucide-react';

// Tier badge component matching design guidelines
const TierBadge = ({ tier, size = 'md' }) => {
  const tiers = {
    BASIC: { icon: Shield, color: '#86858D', name: 'Basic' },
    VERIFIED: { icon: BadgeCheck, color: '#5B8AF0', name: 'Verified' },
    DISTINGUISHED: { icon: Award, color: '#1B5AFF', name: 'Distinguished' },
    ELITE: { icon: Crown, color: '#F0A030', name: 'Elite' }
  };
  
  const tierInfo = tiers[tier?.current] || tiers.BASIC;
  const Icon = tierInfo.icon;
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-[8px]',
    md: 'px-3 py-1.5 text-[9px]'
  };
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 rounded-full border font-mono uppercase tracking-[0.12em] ${sizeClasses[size]}`}
      style={{ 
        backgroundColor: `${tierInfo.color}12`, 
        borderColor: `${tierInfo.color}40`,
        color: tierInfo.color
      }}
      data-testid="professional-tier-badge"
    >
      <Icon className="w-3 h-3" />
      <span className="font-medium">{tier?.name || tierInfo.name}</span>
    </div>
  );
};

// Score bar component
const ScoreBar = ({ score, label }) => (
  <div className="flex items-center gap-3">
    <span className="text-[8px] font-mono uppercase tracking-[0.12em] text-[#707080] w-12">{label}</span>
    <div className="flex-1 h-[5px] bg-[#3A3D48] rounded-full overflow-hidden">
      <div 
        className="h-full rounded-full bg-gradient-to-r from-[#1B5AFF] to-[#F08080]"
        style={{ width: `${score}%` }}
      />
    </div>
    <span className="font-mono text-sm font-medium text-[#0A0F1E] w-8 text-right">{score}</span>
  </div>
);

// Rehire indicator
const RehireIndicator = ({ summary }) => {
  const total = summary.yes + summary.conditional + summary.no;
  if (total === 0) return <span className="text-[#707080] text-sm">No data</span>;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {summary.yes > 0 && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/12 border border-green-500/25">
            <CheckCircle className="w-3 h-3 text-green-400" />
            <span className="text-[10px] font-mono text-green-400">{summary.yes}</span>
          </div>
        )}
        {summary.conditional > 0 && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/12 border border-amber-500/25">
            <Clock className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] font-mono text-amber-400">{summary.conditional}</span>
          </div>
        )}
        {summary.no > 0 && (
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/12 border border-red-500/25">
            <AlertTriangle className="w-3 h-3 text-red-400" />
            <span className="text-[10px] font-mono text-red-400">{summary.no}</span>
          </div>
        )}
      </div>
      <p className="text-[10px] font-mono text-[#707080]">
        {summary.yes_percentage}% rehire eligible
      </p>
    </div>
  );
};

const CandidateComparison = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pipelineCandidates, setPipelineCandidates] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [editingNotes, setEditingNotes] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    // Get employee IDs from URL params
    const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
    if (ids.length > 0) {
      setSelectedIds(ids);
      fetchCandidates(ids);
    } else {
      setLoading(false);
    }
    
    // Fetch pipeline candidates for add modal
    fetchPipelineCandidates();
  }, [searchParams]);

  const fetchCandidates = async (ids) => {
    if (ids.length === 0) {
      setCandidates([]);
      setLoading(false);
      return;
    }
    
    try {
      const res = await employerAPI.compareCandidates(ids);
      setCandidates(res.data.candidates);
    } catch (error) {
      toast.error('Failed to load candidate data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelineCandidates = async () => {
    try {
      const res = await employerAPI.getPipelineCandidates();
      setPipelineCandidates(res.data.candidates);
    } catch (error) {
      // Silent fail
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await employerAPI.searchTalent({ keyword: searchQuery });
      setSearchResults(res.data.results || []);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const addCandidate = (candidate) => {
    const empId = candidate.employee_id || candidate.id;
    if (selectedIds.includes(empId) || selectedIds.length >= 3) {
      toast.error(selectedIds.length >= 3 ? 'Maximum 3 candidates allowed' : 'Already in comparison');
      return;
    }
    
    const newIds = [...selectedIds, empId];
    setSelectedIds(newIds);
    setShowAddModal(false);
    
    // Update URL
    navigate(`/employer/compare?ids=${newIds.join(',')}`, { replace: true });
    fetchCandidates(newIds);
  };

  const removeCandidate = (empId) => {
    const newIds = selectedIds.filter(id => id !== empId);
    setSelectedIds(newIds);
    setCandidates(prev => prev.filter(c => c.employee_id !== empId));
    
    // Update URL
    navigate(`/employer/compare?ids=${newIds.join(',')}`, { replace: true });
  };

  const handleSaveNotes = async () => {
    if (!editingNotes) return;
    setSavingNotes(true);
    try {
      await employerAPI.updateCandidateNotes(editingNotes, notes);
      setCandidates(prev => prev.map(c => 
        c.employee_id === editingNotes ? { ...c, private_notes: notes } : c
      ));
      setEditingNotes(null);
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const openNotesEditor = (candidate) => {
    setEditingNotes(candidate.employee_id);
    setNotes(candidate.private_notes || '');
  };

  const renderStars = (rating) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Star
          key={star}
          className={`w-4 h-4 ${star <= Math.round(rating) ? 'text-[#1B5AFF] fill-[#1B5AFF]' : 'text-[#3A3D48]'}`}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="candidate-comparison-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-[#9A9AA6] hover:text-[#0A0F1E] mb-2 -ml-2"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-[#0A0F1E] font-['Epilogue']">Candidate Comparison</h1>
            <p className="text-[#9A9AA6] mt-1 font-light text-sm sm:text-base">Compare up to 3 candidates side-by-side</p>
          </div>
          {candidates.length < 3 && (
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-md font-mono text-xs tracking-wider uppercase w-full sm:w-auto"
              data-testid="add-candidate-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Candidate
            </Button>
          )}
        </div>

        {/* Empty State */}
        {candidates.length === 0 ? (
          <Card className="bg-[#1E212C] border border-[rgba(255,255,255,0.07)] rounded-xl p-8 sm:p-12 text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#1B5AFF]/10 flex items-center justify-center mx-auto mb-4">
              <User className="w-7 h-7 sm:w-8 sm:h-8 text-[#1B5AFF]" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-[#0A0F1E] font-['Epilogue'] mb-2">No Candidates Selected</h2>
            <p className="text-[#5B6472] font-light mb-6 max-w-md mx-auto text-sm sm:text-base">
              Add candidates from your pipeline or search results to compare them side by side.
            </p>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-md w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Candidate
            </Button>
          </Card>
        ) : (
          /* Comparison Grid - Horizontal scroll on mobile */
          <div className="overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible">
            <div className={`flex sm:grid gap-4 min-w-max sm:min-w-0 ${
              candidates.length === 1 
                ? 'sm:grid-cols-1 sm:max-w-xl' 
                : candidates.length === 2 
                  ? 'sm:grid-cols-2' 
                  : 'sm:grid-cols-3'
            }`}>
            {candidates.map((candidate) => (
              <Card 
                key={candidate.employee_id}
                className="bg-[#1E212C] border border-[rgba(255,255,255,0.07)] rounded-xl overflow-hidden w-[280px] sm:w-auto flex-shrink-0 sm:flex-shrink"
                data-testid={`comparison-card-${candidate.employee_id}`}
              >
                {/* Card Header */}
                <div className="bg-[#0D1016] p-3 sm:p-4 border-b border-[rgba(255,255,255,0.07)] relative">
                  <button
                    onClick={() => removeCandidate(candidate.employee_id)}
                    className="absolute top-2 sm:top-3 right-2 sm:right-3 p-1 rounded-full hover:bg-[#2A2D36] text-[#707080] hover:text-[#0A0F1E] transition-colors"
                    data-testid={`remove-candidate-${candidate.employee_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    {candidate.photo_url ? (
                      <img 
                        src={candidate.photo_url} 
                        alt="" 
                        className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl object-cover border border-[rgba(255,255,255,0.07)]" 
                      />
                    ) : (
                      <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-[#2A2D36] border border-[rgba(255,255,255,0.07)] flex items-center justify-center">
                        <User className="w-5 h-5 sm:w-7 sm:h-7 text-[#707080]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-[#0A0F1E] font-['Epilogue'] truncate">
                        {candidate.first_name} {candidate.last_name}
                      </h3>
                      <p className="text-[#1B5AFF] text-xs sm:text-sm font-medium truncate">{candidate.job_title || 'Professional'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap">
                    <TierBadge tier={candidate.tier} size="sm" />
                    {candidate.vett_id && (
                      <span className="text-[9px] sm:text-[10px] font-mono text-[#707080]">{candidate.vett_id}</span>
                    )}
                  </div>
                </div>

                {/* VETT Score */}
                <div className="p-3 sm:p-4 border-b border-[rgba(255,255,255,0.07)]">
                  <ScoreBar score={candidate.vett_score || 0} label="Score" />
                </div>

                {/* Key Metrics */}
                <div className="p-3 sm:p-4 border-b border-[rgba(255,255,255,0.07)]">
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-[#2A2D36] rounded-lg p-2 sm:p-3">
                      <p className="text-[7px] sm:text-[8px] font-mono uppercase tracking-[0.14em] text-[#707080] mb-1">Verified Records</p>
                      <p className="text-lg sm:text-xl font-bold font-['Epilogue'] text-[#0A0F1E]">{candidate.verified_records_count}</p>
                    </div>
                    <div className="bg-[#2A2D36] rounded-lg p-2 sm:p-3">
                      <p className="text-[7px] sm:text-[8px] font-mono uppercase tracking-[0.14em] text-[#707080] mb-1">Years Exp</p>
                      <p className="text-lg sm:text-xl font-bold font-['Epilogue'] text-[#0A0F1E]">{candidate.years_experience}</p>
                    </div>
                  </div>
                </div>

                {/* Average Rating */}
                <div className="p-3 sm:p-4 border-b border-[rgba(255,255,255,0.07)]">
                  <p className="text-[8px] sm:text-[9px] font-mono uppercase tracking-[0.18em] text-[#9A9AA6] mb-2">Avg Rating</p>
                  <div className="flex items-center gap-2 sm:gap-3">
                    {renderStars(candidate.avg_rating)}
                    <span className="text-base sm:text-lg font-bold font-['Epilogue'] text-[#0A0F1E]">{candidate.avg_rating}</span>
                  </div>
                </div>

                {/* Rehire Eligibility */}
                <div className="p-3 sm:p-4 border-b border-[rgba(255,255,255,0.07)]">
                  <p className="text-[8px] sm:text-[9px] font-mono uppercase tracking-[0.18em] text-[#9A9AA6] mb-2">Rehire Eligibility</p>
                  <RehireIndicator summary={candidate.rehire_summary} />
                </div>

                {/* Top Skills */}
                <div className="p-3 sm:p-4 border-b border-[rgba(255,255,255,0.07)]">
                  <p className="text-[8px] sm:text-[9px] font-mono uppercase tracking-[0.18em] text-[#9A9AA6] mb-2">
                    <Zap className="w-3 h-3 inline mr-1" />
                    Key Skills
                  </p>
                  <div className="flex flex-wrap gap-1 sm:gap-1.5">
                    {candidate.top_skills.slice(0, 6).map((skill) => (
                      <span 
                        key={skill.name}
                        className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded bg-[#1B5AFF]/10 text-[#1B5AFF] text-[9px] sm:text-[10px] font-mono border border-[#1B5AFF]/20"
                      >
                        {skill.name}
                      </span>
                    ))}
                    {candidate.top_skills.length === 0 && (
                      <span className="text-[#707080] text-xs sm:text-sm">No skills data</span>
                    )}
                  </div>
                </div>

                {/* Location & Industry */}
                <div className="p-3 sm:p-4 border-b border-[rgba(255,255,255,0.07)]">
                  <div className="space-y-1.5 sm:space-y-2">
                    {candidate.location && (
                      <div className="flex items-center gap-2 text-[#5B6472] text-xs sm:text-sm">
                        <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#707080]" />
                        {candidate.location}
                      </div>
                    )}
                    {candidate.industry && (
                      <div className="flex items-center gap-2 text-[#5B6472] text-xs sm:text-sm">
                        <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#707080]" />
                        {candidate.industry}
                      </div>
                    )}
                  </div>
                </div>

                {/* Private Notes */}
                <div className="p-3 sm:p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[8px] sm:text-[9px] font-mono uppercase tracking-[0.18em] text-[#9A9AA6]">Private Notes</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openNotesEditor(candidate)}
                      className="text-[#9A9AA6] hover:text-[#0A0F1E] h-6 px-2 text-xs"
                    >
                      Edit
                    </Button>
                  </div>
                  {candidate.private_notes ? (
                    <p className="text-[#5B6472] text-xs sm:text-sm font-light line-clamp-3">{candidate.private_notes}</p>
                  ) : (
                    <p className="text-[#707080] text-xs sm:text-sm italic">No notes yet</p>
                  )}
                </div>

                {/* View Profile Button */}
                <div className="p-3 sm:p-4 pt-0">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/employer/talent/${candidate.employee_id}`)}
                    className="w-full border-[rgba(255,255,255,0.12)] text-[#5B6472] hover:bg-[#2A2D36] rounded-md text-xs sm:text-sm"
                  >
                    <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                    View Full Profile
                  </Button>
                </div>
              </Card>
            ))}
            </div>
          </div>
        )}

        {/* Add Candidate Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-[#1E212C] border-[rgba(255,255,255,0.07)] text-[#0A0F1E] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['Epilogue']">Add Candidate to Compare</DialogTitle>
            </DialogHeader>
            
            {/* Search */}
            <div className="flex gap-2 mb-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#707080]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by name or skill..."
                  className="w-full pl-10 pr-4 py-2 bg-[#2A2D36] border border-[rgba(255,255,255,0.07)] rounded-md text-[#0A0F1E] placeholder:text-[#707080] focus:outline-none focus:border-[rgba(232,80,90,0.5)]"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={searching}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mb-4">
                <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#9A9AA6] mb-2">Search Results</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {searchResults.map((result) => (
                    <div
                      key={result.id}
                      onClick={() => addCandidate(result)}
                      className="flex items-center gap-3 p-3 rounded-lg bg-[#0D1016] hover:bg-[#2A2D36] cursor-pointer transition-colors"
                    >
                      {result.photo_url ? (
                        <img src={result.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#2A2D36] flex items-center justify-center">
                          <User className="w-5 h-5 text-[#707080]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[#0A0F1E] font-medium truncate">{result.first_name} {result.last_name}</p>
                        <p className="text-[#9A9AA6] text-sm truncate">{result.job_title}</p>
                      </div>
                      <Plus className="w-5 h-5 text-[#1B5AFF]" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pipeline Candidates */}
            {pipelineCandidates.length > 0 && (
              <div>
                <p className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#9A9AA6] mb-2">From Your Pipeline</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {pipelineCandidates
                    .filter(c => !selectedIds.includes(c.employee_id))
                    .map((candidate) => (
                    <div
                      key={candidate.id}
                      onClick={() => addCandidate(candidate)}
                      className="flex items-center gap-3 p-3 rounded-lg bg-[#0D1016] hover:bg-[#2A2D36] cursor-pointer transition-colors"
                    >
                      {candidate.photo_url ? (
                        <img src={candidate.photo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#2A2D36] flex items-center justify-center">
                          <User className="w-5 h-5 text-[#707080]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[#0A0F1E] font-medium truncate">{candidate.first_name} {candidate.last_name}</p>
                        <p className="text-[#9A9AA6] text-sm truncate">{candidate.job_title}</p>
                      </div>
                      <Plus className="w-5 h-5 text-[#1B5AFF]" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pipelineCandidates.length === 0 && searchResults.length === 0 && (
              <div className="text-center py-8">
                <User className="w-12 h-12 text-[#707080] mx-auto mb-3" />
                <p className="text-[#9A9AA6]">Search for candidates or add them to your pipeline first</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Notes Editor Modal */}
        <Dialog open={!!editingNotes} onOpenChange={() => setEditingNotes(null)}>
          <DialogContent className="bg-[#1E212C] border-[rgba(255,255,255,0.07)] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle className="font-['Epilogue']">Edit Private Notes</DialogTitle>
            </DialogHeader>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add your private notes about this candidate..."
              className="bg-[#2A2D36] border-[rgba(255,255,255,0.07)] text-[#0A0F1E] rounded-lg min-h-[120px] focus:border-[rgba(232,80,90,0.5)]"
            />
            <p className="text-[10px] font-mono text-[#707080]">
              These notes are private and only visible to you.
            </p>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEditingNotes(null)}
                className="border-[rgba(255,255,255,0.12)] text-[#5B6472]"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
              >
                {savingNotes ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Notes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CandidateComparison;
